/**
 * planGate.ts — Sprint 11 S11.2 (motor v8 §1).
 *
 * Implementa el gate "plan oculto hasta TEST-VAM completado" como
 * derivación pura client-side (Opción B visual del brief v8). El
 * backend sigue generando el plan COMPLETO al POST /training-plans
 * (S11.1); este helper decide si el frontend renderiza el plan entero
 * o solo la card del TEST-VAM como puerta de entrada.
 *
 * REGLAS DE ACTIVACIÓN (todas deben cumplirse):
 *   1. Existe plan activo.
 *   2. Existe onboarding completado con profile cargado.
 *   3. profile.level === 'amateur'.
 *   4. profile.has_watch === true.
 *   5. Existe AL MENOS UNA sesión con session_type='test_vam' y
 *      completion_status='planned'.
 *
 * Si alguna falla → gate inactivo, plan visible normalmente.
 *
 * CASOS QUE INHABILITAN EL GATE (importantes para el UX):
 *   - profile.level === 'beginner': el TEST-VAM no se inyecta para
 *     principiantes (S9.5 lógica `is_amateur_with_watch` en
 *     plan_generator). No hay sesión que esperar.
 *   - profile.has_watch === false: idem; sin reloj no se programa
 *     TEST-VAM.
 *   - test_vam ya `completed`: el atleta ya calibró sus ritmos;
 *     plan completo visible.
 *   - test_vam `skipped`: el atleta optó por no hacerlo (vía PATCH
 *     skip); plan visible aunque sin VAM calibrada (modo sensation).
 *   - No hay sesiones test_vam en absoluto: caso edge (plan generado
 *     pre-S9.5 o profile no-amateur+watch). Sin sesión que esperar.
 *
 * NOTA sobre `profile.level === null`: si el profile existe pero no
 * tiene level set (perfil incompleto), el gate queda INACTIVO. Mejor
 * mostrar plan que bloquear visualmente al atleta sin razón.
 */

import type { OnboardingStatus, TrainingPlan, TrainingSession } from "@/types/api";

export interface PlanGateState {
  /** True si el gate aplica → frontend renderiza solo PreVamGateView.
   *  False → render normal de PlanPage (WeekView + navegación). */
  isActive: boolean;
  /** La sesión TEST-VAM planificada que el atleta debe completar para
   *  desbloquear el plan. Solo presente si `isActive=true`. */
  testVamSession: TrainingSession | null;
  /** Pre-flight F16: el rodaje suave por sensaciones (`rodaje_previo`)
   *  inyectado el día 1 ANTES del test. Es lo que el atleta hace YA
   *  mientras el plan completo sigue oculto. Puede ser null (planes
   *  generados antes de F16 que no lo tengan). */
  introSession: TrainingSession | null;
}

/** Evalúa si el gate "plan oculto pre-VAM" está activo.
 *
 * Pure function — no efectos secundarios, no fetches. Acepta los dos
 * inputs como nullable porque ambos pueden estar en estado de carga
 * cuando PlanPage los pasa al helper.
 */
export function evaluatePlanGate(
  onboarding: OnboardingStatus | null,
  plan: TrainingPlan | null,
): PlanGateState {
  const inactive: PlanGateState = {
    isActive: false,
    testVamSession: null,
    introSession: null,
  };

  if (!plan || !onboarding) return inactive;
  const profile = onboarding.profile;
  if (!profile) return inactive;
  // Sólo amateur con reloj recibe TEST-VAM (S9.5 `is_amateur_with_watch`).
  if (profile.level !== "amateur") return inactive;
  if (!profile.has_watch) return inactive;

  // Buscar la PRIMERA sesión test_vam con completion_status='planned' y, si
  // existe, el rodaje previo (`rodaje_previo`) planned (Pre-flight F16). El
  // plan puede tener varios test_vam (cada 3 meses por v7.1 §3); el gate solo
  // aplica al primero pendiente — los siguientes no bloquean el plan (ya hay
  // VAM calibrada).
  let testVamSession: TrainingSession | null = null;
  let introSession: TrainingSession | null = null;
  for (const week of plan.weeks ?? []) {
    for (const session of week.sessions ?? []) {
      if (
        session.session_type === "test_vam"
        && session.completion_status === "planned"
        && testVamSession === null
      ) {
        testVamSession = session;
      } else if (
        session.session_type === "rodaje_previo"
        && session.completion_status === "planned"
        && introSession === null
      ) {
        introSession = session;
      }
    }
  }

  if (testVamSession === null) return inactive;
  return { isActive: true, testVamSession, introSession };
}
